import { useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

/**
 * Switching views (CO-88, audit N2, B4). Three shapes, one keyboard model:
 *
 * - `Tabs variant="primary"`: the views of a page (Settings). Underlined, the
 *   same look as the project navigation.
 * - `Tabs variant="secondary"`: views inside a section or under a primary
 *   bar (ERP, Deployments/Runs). Visibly smaller and quieter, so two bars
 *   never stack as equals.
 * - `Segmented`: a choice between two to four modes of one control (branch or
 *   commit). Never a primary button as the selected state.
 * - `FilterChips`: narrowing a list, with counts.
 *
 * Tabs follow the ARIA tabs pattern: arrow keys, Home and End move and
 * select; only the selected tab is in the Tab order.
 */

export interface TabOption<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
  testId?: string;
}

function useRovingKeys<T extends string>(options: { value: T }[], value: T, onChange: (value: T) => void) {
  const refs = useRef(new Map<T, HTMLButtonElement>());
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const index = options.findIndex((option) => option.value === value);
    let next = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % options.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + options.length) % options.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = options.length - 1;
    if (next < 0) return;
    event.preventDefault();
    const target = options[next]!.value;
    onChange(target);
    refs.current.get(target)?.focus();
  };
  const ref = (optionValue: T) => (element: HTMLButtonElement | null) => {
    if (element) refs.current.set(optionValue, element);
    else refs.current.delete(optionValue);
  };
  return { onKeyDown, ref };
}

export function Tabs<T extends string>({
  label,
  options,
  value,
  onChange,
  variant = 'primary',
  panelId,
  idBase,
}: {
  /** Names the tab list for assistive technology. */
  label: string;
  options: TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  variant?: 'primary' | 'secondary';
  /** The id of the panel the tabs control. */
  panelId?: string;
  /** Tab ids are `${idBase}-${value}`, for the panel's aria-labelledby. */
  idBase?: string;
}) {
  const generated = useId();
  const base = idBase ?? generated;
  const { onKeyDown, ref } = useRovingKeys(options, value, onChange);
  return (
    <div className={`tabs tabs--${variant}`} role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={ref(option.value)}
            type="button"
            role="tab"
            id={`${base}-${option.value}`}
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            className={`tabs__tab${selected ? ' tabs__tab--active' : ''}`}
            onClick={() => onChange(option.value)}
            data-testid={option.testId}
          >
            {option.label}
            {option.count !== undefined ? (
              <>
                {' '}
                <span className="tabs__count tabular-nums">{option.count}</span>
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
}: {
  label: string;
  options: TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
}) {
  const { onKeyDown, ref } = useRovingKeys(options, value, onChange);
  return (
    <div className={`segmented${size === 'sm' ? ' segmented--sm' : ''}`} role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={ref(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            className={`segmented__option${selected ? ' segmented__option--active' : ''}`}
            onClick={() => onChange(option.value)}
            data-testid={option.testId}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Filter chips narrow a list; each chip says how many rows it shows. */
export function FilterChips<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string; count?: number }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="filter-chips" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="filter-chip"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.count !== undefined ? (
            <>
              {' '}
              <span className="filter-chip__count tabular-nums">{option.count}</span>
            </>
          ) : null}
        </button>
      ))}
    </div>
  );
}
